import React, { useCallback } from "react";
import "../../styles/ExpressionBuilder.css";
import ModalBody from "./ModalBody/ModalBody";
import ModalFooter from "./ModalFooter/ModalFooter";
import ModalHeader from "./ModalHeader/ModalHeader";

/**
 * Main expression builder modal component
 * @param {Function} onClose - Close handler
 * @param {Object} expression - Current expression
 * @param {Function} updateRootExpression - Expression update handler
 * @param {Function} handleSaveExpression - Save handler
 * @param {Function} handleTestExpression - Test expression handler
 * @param {Array} availableFields - Available fields
 * @param {Object} testData - Test data
 * @param {boolean} evaluationResult - Evaluation result
 * @param {Object} fieldValidators - Validation functions for fields
 * @param {boolean} isSubmitting - Whether form is submitting
 * @param {string} submitError - Submission error message
 * @returns {JSX.Element} Expression builder modal
 * @author Abhishek Dhillon
 * @parent SwitchNodePopup | DropdownShowIfPopup
 */
const ExpressionBuilderModal = ({
  onClose,
  expression,
  updateRootExpression,
  handleSaveExpression,
  handleTestExpression,
  availableFields,
  testData,
  evaluationResult,
  fieldValidators = {},
  isSubmitting = false,
  submitError = null,
  modalTitle,
}) => {
  // Add keyboard support for Escape key
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  /**
   * Handles backdrop click to close modal
   * @param {Object} e - Event object
   * @author Abhishek Dhillon
   */
  const handleBackdropClick = useCallback(
    (e) => {
      // Only close if clicking on the backdrop, not the modal content
      if (e.target === e.currentTarget && e.target.classList.contains("expression-builder-modal-backdrop")) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <div className='expression-builder-modal-backdrop' onClick={handleBackdropClick}>
      <div className='expression-builder-modal-content'>
        {/* Modal Header Component */}
        <ModalHeader onClose={onClose} title={modalTitle} />

        {/* Show submission error if any */}
        {submitError && (
          <div className='alert alert-danger mx-3 mt-3' role='alert'>
            {submitError}
          </div>
        )}

        {/* Modal Body Component */}
        <ModalBody
          expression={expression}
          updateRootExpression={updateRootExpression}
          availableFields={availableFields}
          testData={testData}
          evaluationResult={evaluationResult}
          onTestExpression={handleTestExpression}
          fieldValidators={fieldValidators}
        />

        {/* Modal Footer Component */}
        <ModalFooter
          onClose={onClose}
          handleSaveExpression={handleSaveExpression}
          isSaveDisabled={!expression?.children?.length || isSubmitting}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
};

export default ExpressionBuilderModal;
